import { Button } from '@/components/ui/button';
import { HardDrive, Check, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useGoogleDriveAuth } from '@/features/auth/hooks/useGoogleDriveAuth';
import { toast } from 'sonner';
import { useAppSelector } from '@/store/hooks';
import { selectGoogleDriveConnection } from '@/features/auth/store/selectors';

/**
 * Google Drive Authorization Component
 *
 * Two-tier authentication strategy:
 * 1. "Làm mới token" - Calls backend API to refresh token (no popup)
 * 2. "Kết nối lại" - Full OAuth flow (only when refresh fails)
 */
interface GoogleDriveAuthProps {
  forceReauthorize?: boolean;
  onConnected?: () => void;
}

export const GoogleDriveAuth = ({ forceReauthorize = false, onConnected }: GoogleDriveAuthProps) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isReauthorizing, setIsReauthorizing] = useState(false);
  const { hasAccess, refreshAccessToken, ensureAccess, requestAccess } = useGoogleDriveAuth();
  const googleDrive = useAppSelector(selectGoogleDriveConnection);

  // Check if already authorized on mount
  useEffect(() => {
    setIsAuthorized(hasAccess());
  }, [hasAccess]);

  /**
   * Handle refresh token via backend API (no OAuth popup)
   */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const token = await refreshAccessToken();

      if (token) {
        setIsAuthorized(true);
        onConnected?.();
        toast.success('Đã làm mới Google Drive token thành công!');
      } else {
        toast.error('Không thể làm mới token. Vui lòng kết nối lại.');
      }
    } catch {
      toast.error('Lỗi khi làm mới token. Vui lòng kết nối lại.');
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Handle connection - try refresh first, then OAuth popup if needed
   * This uses ensureAccess() which:
   * 1. Check if token exists and not expiring -> use it
   * 2. Try to refresh token via backend API first
   * 3. Only trigger OAuth popup if refresh fails
   *
   * Note: Toast messages are handled in useGoogleDriveAuth hook
   */
  const handleConnect = async () => {
    setIsReauthorizing(true);
    if (forceReauthorize) {
      requestAccess((connected) => {
        setIsAuthorized(connected);
        if (connected) onConnected?.();
        setIsReauthorizing(false);
      });
      return;
    }
    try {
      const token = await ensureAccess();

      if (token) {
        setIsAuthorized(true);
        onConnected?.();
        // Toast is already shown in useGoogleDriveAuth hook
      } else {
        // This shouldn't happen (ensureAccess throws error if fails)
        setIsAuthorized(false);
      }
    } catch (error) {
      console.error('Connection error:', error);
      setIsAuthorized(false);
      // Toast error is already shown in useGoogleDriveAuth hook
    } finally {
      setIsReauthorizing(false);
    }
  };

  // Authorized state - show status + refresh button
  if ((isAuthorized || googleDrive.status === 'connected') && !forceReauthorize) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 text-sm text-green-600">
          <Check className="h-4 w-4" />
          <span>Google Drive đã kết nối</span>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={isRefreshing}
          variant="ghost"
          size="sm"
          title="Làm mới token"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    );
  }

  // Not authorized - show connect button
  return (
    <Button
      onClick={handleConnect}
      disabled={isReauthorizing}
      variant="outline"
      size="sm"
      className="w-full md:w-auto"
    >
      <HardDrive className="mr-2 h-4 w-4" />
      {isReauthorizing ? 'Đang kết nối...' : 'Kết nối Google Drive'}
    </Button>
  );
};
